import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Search, Upload, MessageCircle, Bot } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, EASE } from "./Reveal";
import { scrollToId } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const HERO_IMG = "/files/hero-copper-cable.webp";

const WA = "https://wa.me/919008177176";

const API = process.env.REACT_APP_BACKEND_URL;

function usePriceTicker() {
    const [ticker, setTicker] = useState({ count: null, items: [], idx: 0 });
    useEffect(() => {
        let alive = true;
        fetch(`${API}/api/prices/ticker`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (alive && d) setTicker((t) => ({ ...t, count: d.count, items: d.items || [] })); })
            .catch(() => {});
        const iv = setInterval(() => setTicker((t) => ({ ...t, idx: t.items.length ? (t.idx + 1) % t.items.length : 0 })), 3500);
        return () => { alive = false; clearInterval(iv); };
    }, []);
    return ticker;
}

function RotatingBadge() {
    return (
        <div className="absolute -top-8 -right-4 md:-right-10 w-24 h-24 md:w-28 md:h-28 z-10" data-testid="hero-badge">
            <svg viewBox="0 0 120 120" className="w-full h-full animate-spin-slow drop-shadow-lg">
                <defs>
                    <path id="circlePath" d="M 60,60 m -44,0 a 44,44 0 1,1 88,0 a 44,44 0 1,1 -88,0" />
                </defs>
                <circle cx="60" cy="60" r="58" fill="#14161B" />
                <text fill="#3B82F6" fontSize="10.5" fontFamily="JetBrains Mono, monospace" letterSpacing="2.5">
                    <textPath href="#circlePath">ROXY CABLES • CABLES &amp; WIRES • BENGALURU</textPath>
                </text>
                <circle cx="60" cy="60" r="10" fill="none" stroke="#3B82F6" strokeWidth="1.5" />
                <circle cx="60" cy="60" r="3" fill="#3B82F6" />
            </svg>
        </div>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const [q, setQ] = useState("");
    const ticker = usePriceTicker();
    const rate = ticker.items[ticker.idx];
    const navigate = useNavigate();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

    const goSearch = (e) => {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("roxy:search", { detail: q }));
        scrollToId("products");
    };

    const askAI = () =>
        toast.info("Roxy AI assistant is launching soon", {
            description: "Phase 3 rollout — meanwhile our desk answers on WhatsApp in minutes.",
        });

    return (
        <section ref={ref} className="relative min-h-screen hud-bg overflow-hidden" data-testid="hero-section">
            <div className="hud-scan" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-28 md:pt-36 pb-10 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7">
                    <motion.button
                        onClick={() => navigate("/prices")}
                        data-testid="hero-live-beacon"
                        className="live-beacon group relative mb-7 inline-flex items-center gap-3.5 rounded-full border border-copper/50 bg-white/70 backdrop-blur-md pl-4 pr-6 py-3 overflow-hidden focus:outline-none focus-visible:border-copper"
                        initial={{ opacity: 0, y: 16, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <span className="live-beacon-sweep" aria-hidden="true" />
                        <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                            <span className="live-beacon-ping absolute inline-flex h-full w-full rounded-full bg-money" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-money" />
                        </span>
                        <span className="relative font-mono text-[11px] md:text-xs tracking-[0.28em] uppercase text-ink whitespace-nowrap">
                            <span className="text-copper font-bold">Live prices.</span>{" "}
                            Instant estimate.
                        </span>
                        <span className="relative font-mono text-[9px] tracking-[0.2em] uppercase text-dim hidden lg:inline whitespace-nowrap group-hover:text-copper transition-colors duration-300">
                            {ticker.count ? `${ticker.count} rates on tap` : "rates on tap"}
                            {rate && (
                                <span key={ticker.idx} className="beacon-rate text-money" data-testid="hero-beacon-rate">
                                    {" "}· {rate.label} ₹{Number(rate.price).toLocaleString("en-IN")}/{rate.unit === "metre" ? "m" : rate.unit}
                                </span>
                            )}
                            {" →"}
                        </span>
                    </motion.button>

                    <motion.p
                        className="font-mono text-[11px] md:text-xs tracking-[0.35em] uppercase text-copper mb-8 flex items-center gap-3"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
                        data-testid="hero-eyebrow"
                    >
                        <span className="h-px w-10 bg-copper-bright/70" />
                        B2B Cables, Wires &amp; Accessories — Bengaluru
                    </motion.p>

                    <MaskedLines
                        as="h1"
                        className="font-display font-bold leading-[0.98] tracking-tight text-[clamp(3rem,8vw,7rem)] text-ink"
                        lineClassName="will-change-transform"
                        delay={0.15}
                        lines={[
                            <span key="a">Every grid</span>,
                            <span key="b">starts with</span>,
                            <span key="c" className="italic text-copper">good wire.</span>,
                        ]}
                    />

                    <motion.p
                        className="mt-8 max-w-xl text-mist text-base md:text-lg leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                        data-testid="hero-subline"
                    >
                        HT &amp; LT cables, house wires, glands, lugs and jointing kits from KEI, Polycab,
                        Havells, Dowells, Raychem and 3M — stocked in Chickpet, Bengaluru,
                        with dispatch timelines confirmed on every quotation.
                    </motion.p>

                    <motion.form
                        onSubmit={goSearch}
                        className="mt-9 flex max-w-xl"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
                    >
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-dim" />
                            <input
                                data-testid="hero-search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder='Search "95 sqmm 11 kV", "Dowells lug", "FRLS"...'
                                className="w-full bg-white/70 border border-line rounded-l-full pl-12 pr-4 py-4 text-sm text-ink placeholder:text-dim focus:outline-none focus:border-copper-bright transition-colors duration-300 backdrop-blur-sm"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="hero-search-btn"
                            className="rounded-r-full bg-copper text-white font-mono uppercase tracking-[0.15em] text-[11px] px-6 hover:bg-copper-bright transition-colors duration-300"
                        >
                            Search
                        </button>
                    </motion.form>

                    <motion.div
                        className="mt-6 flex flex-wrap items-center gap-3"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
                    >
                        <button
                            data-testid="hero-cta-prices"
                            onClick={() => navigate("/prices")}
                            className="group rounded-full bg-copper text-white font-mono uppercase tracking-[0.15em] text-[10px] px-6 py-3 hover:bg-copper-bright shadow-[0_0_24px_rgba(29,78,216,0.35)] transition-all duration-300"
                        >
                            Check Cable Prices &amp; Get Instant Estimate
                            <ArrowDownRight size={13} className="inline ml-1.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
                        </button>
                        <button
                            data-testid="hero-cta-enquiry"
                            onClick={() => scrollToId("enquiry")}
                            className="group rounded-full bg-white text-inkdark dark:bg-white/10 dark:text-white border border-transparent dark:border-white/15 font-mono uppercase tracking-[0.15em] text-[10px] px-6 py-3 hover:bg-copper hover:text-white hover:border-copper transition-colors duration-300"
                        >
                            Request Quote
                            <ArrowDownRight size={13} className="inline ml-1.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
                        </button>
                        <a
                            data-testid="hero-cta-boq"
                            href="/upload-boq"
                            className="rounded-full border border-line text-ink font-mono uppercase tracking-[0.15em] text-[10px] px-6 py-3 hover:border-copper-bright hover:text-copper-bright transition-colors duration-300 flex items-center gap-2"
                        >
                            <Upload size={13} /> Upload BOQ
                        </a>
                        <a
                            data-testid="hero-cta-whatsapp"
                            href={WA}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-full border border-line text-ink font-mono uppercase tracking-[0.15em] text-[10px] px-6 py-3 hover:border-[#25D366] hover:text-[#25D366] transition-colors duration-300 flex items-center gap-2"
                        >
                            <MessageCircle size={13} /> WhatsApp
                        </a>
                        <button
                            data-testid="hero-cta-ai"
                            onClick={askAI}
                            className="rounded-full border border-copper/50 bg-copper/15 text-copper-bright font-mono uppercase tracking-[0.15em] text-[10px] px-6 py-3 hover:bg-copper hover:text-white transition-colors duration-300 flex items-center gap-2"
                        >
                            <Bot size={13} /> Ask Roxy AI
                        </button>
                    </motion.div>

                    <motion.p
                        className="mt-5 font-mono text-[10px] tracking-[0.15em] uppercase text-dim"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.05 }}
                    >
                        +91 900 81 77 176 · +91 080 41689333 · Chickpet, Bengaluru 560053
                    </motion.p>
                </div>

                <motion.div
                    className="lg:col-span-5 relative"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
                    onMouseMove={(e) => {
                        const el = e.currentTarget.querySelector("[data-hero-tilt]");
                        if (!el) return;
                        const r = e.currentTarget.getBoundingClientRect();
                        const px = (e.clientX - r.left) / r.width - 0.5;
                        const py = (e.clientY - r.top) / r.height - 0.5;
                        el.style.transform = `perspective(1000px) rotateX(${(-py * 4).toFixed(2)}deg) rotateY(${(px * 5).toFixed(2)}deg)`;
                    }}
                    onMouseLeave={(e) => {
                        const el = e.currentTarget.querySelector("[data-hero-tilt]");
                        if (el) el.style.transform = "";
                    }}
                >
                    <div data-hero-tilt className="transition-transform duration-300 ease-out will-change-transform">
                    <RotatingBadge />
                    <motion.div
                        style={{ y: imgY }}
                        className="relative aspect-[4/5] max-h-[68vh] w-full overflow-hidden rounded-md border border-line shadow-[0_24px_70px_rgba(0,0,0,0.55),0_0_50px_rgba(29,78,216,0.12)]"
                        data-testid="hero-image"
                    >
                        <img
                            src={HERO_IMG}
                            alt="Macro view of stranded copper conductor"
                            className="w-full h-full object-cover"
                            fetchpriority="high"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-inkdark/80 via-transparent to-inkdark/20" />
                        <span className="hud-frame" aria-hidden="true" />
                        <div className="absolute bottom-0 inset-x-0 p-6 flex justify-between items-end font-mono text-[10px] tracking-[0.2em] text-white/70 uppercase">
                            <span>FIG. 01 — CLASS 2 STRANDED Cu</span>
                            <span className="text-copper-bright">99.99% ETP</span>
                        </div>
                    </motion.div>

                    <motion.div
                        className="absolute -left-4 md:-left-12 bottom-10 bg-inkdark/85 backdrop-blur border border-copper/40 rounded-md px-5 py-4 shadow-[0_0_30px_rgba(29,78,216,0.25)]"
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
                        data-testid="hero-spec-card"
                    >
                        <p className="font-mono text-[10px] tracking-[0.25em] text-mist uppercase mb-1">Conductivity</p>
                        <p className="font-mono text-sm text-copper-bright">0.0172 Ω·mm²/m @ 20°C</p>
                    </motion.div>
                    </div>
                </motion.div>
            </div>

            <motion.div
                className="relative border-t border-line"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-dim">
                    <span>99.99% ETP Copper Conductor</span>
                    <span>XLPE / PVC / LSZH Build</span>
                    <span>IS 7098 · IEC 60502 · EN 50618</span>
                    <span className="text-copper-bright">Dispatch confirmed per quote · South India</span>
                </div>
            </motion.div>
        </section>
    );
}
